import type { Country } from '@/constants/country';
import {
  ChassisNumberDisplayMode,
  DEFAULT_VEHICLE_IDENTIFIER_SETTINGS,
  PrimaryVehicleIdentifier,
  type VehicleIdentifierSettings,
} from '@/constants/vehicle-identifier';

export interface VehicleIdentifierInput {
  licensePlate?: string | null;
  chassisNumber?: string | null;
  country?: Country | null;
}

export type ResolvedVehicleIdentifier =
  | { kind: 'license-plate'; value: string; country?: Country }
  | { kind: 'chassis'; value: string; truncated: boolean }
  | { kind: 'none' };

const hasValue = (value?: string | null): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const resolveVehicleIdentifier = (
  vehicle: VehicleIdentifierInput,
  settings: VehicleIdentifierSettings = DEFAULT_VEHICLE_IDENTIFIER_SETTINGS,
): ResolvedVehicleIdentifier => {
  const licensePlate = hasValue(vehicle.licensePlate) ? vehicle.licensePlate : undefined;
  const chassisNumber = hasValue(vehicle.chassisNumber) ? vehicle.chassisNumber : undefined;
  const truncated = settings.chassisNumberDisplayMode === ChassisNumberDisplayMode.LastSixCharacters;

  const primary = settings.primaryVehicleIdentifier;
  const preferLicensePlate = primary === PrimaryVehicleIdentifier.LicensePlate;

  if (preferLicensePlate) {
    if (licensePlate) {
      return { kind: 'license-plate', value: licensePlate, country: vehicle.country ?? undefined };
    }
    if (chassisNumber) {
      return { kind: 'chassis', value: chassisNumber, truncated };
    }
  } else {
    if (chassisNumber) {
      return { kind: 'chassis', value: chassisNumber, truncated };
    }
    if (licensePlate) {
      return { kind: 'license-plate', value: licensePlate, country: vehicle.country ?? undefined };
    }
  }

  return { kind: 'none' };
};
